/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './video-landing.stache';
import device from 'scripts/utils/device';
import can from 'can';
import settings from 'scripts/utils/settings';
import appContext from 'scripts/app-context';
import _ from 'lodash';

const moduleSettings = settings.getModuleSettings('live');
const isLiveEnabled = moduleSettings.isEnabled;
const disabledMessage = moduleSettings.unavailabilityMessage;
const inWebView = window.globalSettings.inWebView;

const viewingScheduleUrl = window.globalSettings.pagePaths.viewingSchedule;

const getDrawerVisibility = (data) => {
    const activeVideos = _.get(data, 'activeView.videos', []);
    const currentCard = data.cards.find((card) =>
        activeVideos.includes(card.video.identifier),
    );

    if (!currentCard) {
        return false;
    }

    if (
        currentCard.channelIdentifier &&
        currentCard.channelIdentifier.includes('featured-hole')
    ) {
        return Boolean(_.get(currentCard, 'holeTags.length'));
    }
    return Boolean(_.get(currentCard, 'playerTagGroups.length'));
};

Component.extend({
    tag: 'video-landing',
    template: template,
    leakScope: false,
    viewModel: {
        viewingScheduleUrl,
        sponsorIsAvailable: false,
        define: {
            isEnabled: {
                type: 'boolean',
                value: isLiveEnabled,
            },
            disabledMessage: {
                type: 'string',
                value: disabledMessage,
            },
            products: {
                Type: can.Map,
                Value: null,
            },
            productsCtaLink: {
                Type: can.Map,
                Value: null,
            },
            highlightEnded: {
                type: 'number',
            },
            sponsor: {
                Type: can.Map,
                Value: null,
            },
            compatibleVideoPlayer: {
                type: 'boolean',
                value: true,
            },
            liveVideoCards: {
                Type: can.List,
                Value: can.List,
            },
            streamOffMessaging: {
                Type: can.Map,
                Value: can.Map,
            },
            streamOffMessage: {
                Type: can.Map,
                Value: null,
            },
            activeChannels: {
                Type: can.List,
                Value: can.List,
            },
            activeView: {
                type: 'object',
                value: {},
            },
            cards: {
                type: 'array',
                value: [],
            },
            isMobileWeb: {
                type: 'boolean',
                get() {
                    return (
                        ['small', 'extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) > -1 && !inWebView
                    );
                },
            },
            isVisibleDesktop: {
                type: 'boolean',
                get() {
                    return (
                        ['medium', 'small', 'extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) === -1
                    );
                },
            },
            dataLoaded: {
                type: 'boolean',
                value: false,
            },
            isInPip: {
                type: 'boolean',
                value: false,
            },
            drawerActive: {
                type: 'boolean',
            },
            player: {
                type: 'object',
            },
        },
        onDataLoaded() {
            this.attr('dataLoaded', true);
        },
        handleViewChange(ev, el, { isInPip }) {
            this.attr('isInPip', isInPip);
        },
    },
    init() {
        const drawerCompute = can.compute(() => ({
            activeView: this.viewModel.attr('activeView').serialize(),
            cards: this.viewModel.attr('cards').serialize(),
        }));

        drawerCompute.bind('change', (_, value) => {
            this.viewModel.attr('drawerActive', getDrawerVisibility(value));
        });

        let incompatibleVideoPlayer = '';

        if (device.isModernIE || device.isOldIE) {
            incompatibleVideoPlayer =
                'INTERNET EXPLORER IS NOT SUPPORTED, FOR OPTIMAL STREAMING PLEASE CHOOSE ANOTHER BROWSER SUCH AS FIREFOX OR CHROME';
        }
        if (incompatibleVideoPlayer) {
            this.viewModel.attr('compatibleVideoPlayer', false);
            this.viewModel.attr('streamOffMessage', {
                videoStreamingOn: false,
                titleText: '',
                streamNotAvailableMessage: incompatibleVideoPlayer,
            });
        } else {
            this.viewModel.attr('liveVideoCards').on('change', () => {
                const cards = this.viewModel.attr('liveVideoCards');
                const statuses = ['live', 'upcoming', 'concluded'];
                if (cards && cards.length) {
                    let featuredEvent = null;
                    statuses.forEach((status) => {
                        if (!featuredEvent) {
                            cards.each((card) => {
                                if (card.streamState == status) {
                                    featuredEvent = card;
                                    return false;
                                }
                            });
                        }
                    });
                    this.viewModel.attr('featuredEvent', featuredEvent);
                }
            });
        }
    },
});
